import React from "react";
import { Link } from "react-router-dom";
import {
  XIcon,
  UsersIcon,
  CurrencyDollarIcon,
  GiftIcon,
  ChatCircleIcon,
  GavelIcon,
  ClockCountdownIcon,
  PackageIcon,
  CheckCircleIcon,
  ShareIcon,
} from "@phosphor-icons/react";

function fmtDuration(seconds) {
  if (!seconds || seconds < 0) return "0m";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function Stat({ icon: Icon, label, value, sub, accent = "text-white" }) {
  return (
    <div className="rounded-2xl bg-zinc-800/60 border border-zinc-700 p-4">
      <div className="flex items-center gap-2 text-[10px] font-mono tracking-widest uppercase text-zinc-400">
        <Icon size={12} weight="fill" /> {label}
      </div>
      <div className={`font-display font-black text-2xl sm:text-3xl mt-1 tracking-tight ${accent}`}>
        {value}
      </div>
      {sub ? <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div> : null}
    </div>
  );
}

export default function StreamRecapModal({ summary, onClose }) {
  if (!summary) return null;

  const {
    title,
    duration_seconds,
    peak_viewers,
    items_sold,
    revenue,
    bid_count,
    tip_count,
    tip_total,
    unique_chatters,
    listings_featured,
    tap_count = 0,
    stream_id,
  } = summary;

  const shareText = `Just wrapped a live show on Bidorah${title ? ` — "${title}"` : ""}. ${
    peak_viewers
  } peak viewers, ${items_sold} items sold, ${fmtMoney(revenue)} in sales.`;

  const share = async () => {
    const wrapUrl = `/api/streams/${stream_id}/wrap-card.png`;
    // Best path: Web Share API Level 2 — share the PNG image directly.
    try {
      const res = await fetch(wrapUrl, { credentials: "include" });
      if (!res.ok) throw new Error("fetch_failed");
      const blob = await res.blob();
      const file = new File([blob], `bidorah-wrap-${stream_id?.slice(-8) || "wrap"}.png`, {
        type: "image/png",
      });
      if (
        typeof navigator !== "undefined" &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          title: "Bidorah live show wrap",
          text: shareText,
          files: [file],
        });
        return;
      }
      // Fallback #1: download the image so the user can manually attach it.
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      // Also copy the text to clipboard so a paste into IG/X is one step away.
      if (navigator.clipboard) navigator.clipboard.writeText(shareText);
    } catch {
      // Fallback #2: plain Web Share (text only) or clipboard copy.
      if (navigator.share) {
        try {
          await navigator.share({ title: "Bidorah live show wrap", text: shareText });
          return;
        } catch {}
      }
      if (navigator.clipboard) {
        navigator.clipboard.writeText(shareText);
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm grid place-items-center p-4 animate-in fade-in"
      data-testid="stream-recap-modal"
      onClick={onClose}
      style={{
        // Honour device safe-areas so the modal never sits under the
        // status bar / home indicator on phones.
        paddingTop: "max(env(safe-area-inset-top, 0px), 1rem)",
        paddingBottom: "max(env(safe-area-inset-bottom, 0px), 1rem)",
      }}
    >
      <div
        className="relative w-full max-w-lg bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — tight padding so the blue band stops cleanly right
            below the metadata, never crowding the stat boxes. Stats sit
            below with normal flow (no negative overlap) so the "1" and
            "$0 / 0 items sold" labels are always fully visible on tall
            mobile screens. */}
        <div className="relative bg-gradient-to-br from-[#1D78AB] via-[#165d85] to-[#0E3F5C] px-5 pt-4 pb-4 text-white">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-full bg-white/15 hover:bg-white/25 text-white transition"
            aria-label="Close"
            data-testid="stream-recap-close"
          >
            <XIcon size={16} weight="bold" />
          </button>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase bg-white/15 rounded-full px-2 py-0.5 mr-12">
            <CheckCircleIcon size={11} weight="fill" /> Stream ended
          </div>
          <h2 className="font-display font-black text-xl sm:text-2xl tracking-tighter leading-tight mt-2 pr-12">
            {title || "Thanks for going live."}
          </h2>
          <div className="flex items-center gap-1.5 mt-1 text-[11px] text-white/70">
            <ClockCountdownIcon size={12} weight="fill" />
            <span>{fmtDuration(duration_seconds)} on air</span>
            <span>·</span>
            <span>
              {listings_featured} listing{listings_featured === 1 ? "" : "s"} featured
            </span>
          </div>
        </div>

        {/* Stats grid — sits cleanly below the header in normal flow,
            no negative margin so the blue gradient never covers any
            stat label. */}
        <div className="px-5 mt-3 grid grid-cols-2 gap-3">
          <Stat
            icon={UsersIcon}
            label="Peak viewers"
            value={peak_viewers.toLocaleString()}
            accent="text-white"
          />
          <Stat
            icon={CurrencyDollarIcon}
            label="Revenue"
            value={fmtMoney(revenue)}
            sub={`${items_sold} item${items_sold === 1 ? "" : "s"} sold`}
            accent="text-emerald-400"
          />
          <Stat
            icon={GavelIcon}
            label="Bids placed"
            value={bid_count.toLocaleString()}
            accent="text-white"
          />
          <Stat
            icon={GiftIcon}
            label="Tips earned"
            value={fmtMoney(tip_total)}
            sub={`${tip_count} tip${tip_count === 1 ? "" : "s"}`}
            accent="text-amber-300"
          />
          <Stat
            icon={ChatCircleIcon}
            label="Unique chatters"
            value={unique_chatters.toLocaleString()}
            accent="text-white"
          />
          <Stat
            icon={ChatCircleIcon}
            label="Tap reactions"
            value={tap_count.toLocaleString()}
            sub="❤️ 🔥 👏 from viewers"
            accent="text-pink-300"
          />
          <Stat
            icon={PackageIcon}
            label="Sell-through"
            value={
              listings_featured > 0
                ? `${Math.round((items_sold / listings_featured) * 100)}%`
                : "—"
            }
            sub={
              listings_featured > 0
                ? `${items_sold}/${listings_featured}`
                : "No listings tagged"
            }
            accent="text-white"
          />
        </div>

        {/* Actions */}
        <div className="p-5 mt-5 space-y-2 bg-zinc-900">
          <button
            onClick={share}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white text-zinc-900 hover:bg-zinc-200 font-semibold text-sm"
            data-testid="stream-recap-share"
          >
            <ShareIcon size={14} weight="bold" /> Share wrap
            <span className="text-[10px] font-mono text-zinc-500 ml-1">
              · image + text
            </span>
          </button>
          <div className="grid grid-cols-2 gap-2">
            <Link
              to="/orders"
              className="inline-flex items-center justify-center px-4 py-2.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-semibold"
              onClick={onClose}
              data-testid="stream-recap-orders"
            >
              View orders
            </Link>
            <Link
              to="/payouts"
              className="inline-flex items-center justify-center px-4 py-2.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-semibold"
              onClick={onClose}
              data-testid="stream-recap-payouts"
            >
              View payouts
            </Link>
          </div>
          <button
            onClick={onClose}
            className="w-full text-[11px] text-zinc-500 hover:text-zinc-300 py-2"
            data-testid="stream-recap-dismiss"
          >
            Close
          </button>
          {stream_id && (
            <div className="text-[10px] text-zinc-600 font-mono text-center pt-1">
              Stream · {stream_id.slice(-8).toUpperCase()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
